<template>
  <div :class="bem()">
    <ul v-if="standardHeroDesigns?.length" :class="bem('row', { standard: true })">
      <li
        v-for="(variant, index) in standardHeroDesigns"
        :key="index"
        :class="
          bem('row-item', {
            active: props.activeSwatchIndex === index,
          })
        "
      >
        <button
          :class="bem('row-btn')"
          @click="emits('active-swatch-index-changed', index)"
        >
          <DynamicImg
            v-if="variant.legacySKU"
            :sku="variant.legacySKU"
            :width="160"
            :height="160"
            bg="transparent"
            :view-angle="2"
          />
          <span
            v-if="variant.swatchIcon"
            :class="bem('badge')"
          >
            <img :src="variant.swatchIcon" />
          </span>
        </button>
      </li>
      <li :class="bem('row-item', { custom: true, active: props.customButtonActive })">
        <button
          :class="bem('row-btn')"
          @click="emits('toggled-customiser-mode')"
        >
          <span :class="bem('custom-icon-wrapper')">
            <CustomIcon id="image-swatch-select" :class="bem('custom-icon')"/>
          </span>

          <span :class="bem('custom-icon-text')">Custom</span>
        </button>
      </li>
    </ul>
    <ul v-if="limitedEditionDesigns?.length" :class="bem('row', { limitedEdition: true })">
      <li
        v-for="(variant, index) in limitedEditionDesigns"
        :key="index"
        :class="
          bem('row-item', {
            active:
              props.activeSwatchIndex === standardHeroDesigns.length + index,
          })
        "
      >
        <button
          :class="bem('row-btn')"
          @click="
            emits(
              'active-swatch-index-changed',
              standardHeroDesigns.length + index
            )
          "
        >
          <DynamicImg
            v-if="variant.legacySKU"
            :sku="variant.legacySKU"
            :width="160"
            :height="160"
            bg="transparent"
            :view-angle="2"
          />
          <span
            v-if="variant.swatchIcon"
            :class="bem('badge')"
          >
            <img :src="variant.swatchIcon" />
          </span>
        </button>
      </li>
      <li v-if="!pageStore.showAllLimitedEditionSwatches && additionalLimitedEditionDesignsToShow > 0" :class="bem('row-item', { showMore: true })">
        <button
          :class="bem('row-btn')"
          @click="pageStore.toggleShowAllLimitedEditionSwatches(true)"
        >
          <span :class="bem('custom-icon-wrapper')"> +{{ additionalLimitedEditionDesignsToShow }} </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
const bem = useBEM('ImageSwatchSelect')

const emits = defineEmits([
  'active-swatch-index-changed',
  'toggled-customiser-mode',
])


const pageStore = usePageStore()

interface ImageSwatchSelectProps {
  variants: Array<any> | never[]
  activeSwatchIndex: number
  customButtonActive: boolean
  limitedEditionCount: number
  isLimitedEdition: boolean
}

const props = withDefaults(defineProps<ImageSwatchSelectProps>(), {
  variants: () => [],
  activeSwatchIndex: 0,
  customButtonActive: false,
  limitedEditionCount: 0,
  isLimitedEdition: false,
  customiserOpen: false,
})

const standardHeroDesigns = computed(() => {
  return props.variants
    .filter((variant) => !variant.isLimitedEdition)
    .slice(0, 6)
})

const totalLimitedEditionDesigns = computed(() => {
  return props.variants.filter((variant) => variant.isLimitedEdition).length
})

const additionalLimitedEditionDesignsToShow = computed(() => {
  return totalLimitedEditionDesigns.value - limitedEditionDesigns.value.length
})

const limitedEditionDesigns = computed(() => {
  return pageStore.showAllLimitedEditionSwatches
    ? props.variants.filter((variant) => variant.isLimitedEdition)
    : props.variants.filter((variant) => variant.isLimitedEdition).slice(0, 6)
})
</script>

<style lang="postcss" scoped>
.ImageSwatchSelect {
  @apply grid gap-y-[10px] gap-2;

  grid-template-areas: 'standard-designs' 'limited-edition-designs';
}

.ImageSwatchSelect__row {
  @apply grid grid-cols-7 gap-x-[5px] gap-y-[10px] md:gap-2;
}

.ImageSwatchSelect__row--standard {
  grid-area: standard-designs;
}

.ImageSwatchSelect__row--limited-edition {
  grid-area: limited-edition-designs;
}

.ImageSwatchSelect__row-item {
  @apply aspect-square bg-white;

  .is-dark-chrome-theme & {
    @apply bg-off-black;
  }
}

.ImageSwatchSelect__row-item--active {
  .ImageSwatchSelect__row-btn,
  .ImageSwatchSelect__row-btn:hover {
    @apply border-black;

    .is-dark-chrome-theme & {
      @apply border-white;
    }
  }
}

.ImageSwatchSelect__row-item--custom {
  grid-column: 7;
}

.ImageSwatchSelect__row-item--show-more {
  @apply text-xxs text-grey-800 font-fragment-mono;
}

.ImageSwatchSelect__row-btn {
  @apply relative w-full aspect-square
    flex items-center justify-center flex-col gap-y-1
    border border-grey-400 hover:border-grey rounded-[0.25rem];

  .is-dark-chrome-theme & {
    @apply border-grey-700 hover:border-white;
  }

  &:hover {
    .ImageSwatchSelect__badge {
      @apply border-grey;

      .is-dark-chrome-theme & {
        @apply border-white;
      }
    }
  }
}

.ImageSwatchSelect__custom-icon-wrapper {
  @apply w-4/12;

  .ImageSwatchSelect__custom-icon {
    @apply fill-black;
  }

  .is-dark-chrome-theme & {
    .ImageSwatchSelect__custom-icon {
      @apply fill-white;
    }
  }
}

.ImageSwatchSelect__custom-icon-text {
  @apply font-fragment-mono text-tiny;
}

.ImageSwatchSelect__badge {
  @apply w-4/12
    absolute bottom-0 translate-y-1/2
    overflow-hidden
    rounded-full border border-grey-400;
}
</style>
